import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Grid2, Typography, Button, Paper, Chip, IconButton } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {
  Target,
  Compass,
  Cpu,
  Building2,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import LogoImg from '../assets/AcoSignFavIcon.png';

const AboutPage = () => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* ========================================================================= */}
      {/* 1. ABOUT HERO (Pill Removed, Emerald-Blue Gradient Highlight) */}
      {/* ========================================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle Engineering Grid Background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(rgba(16, 185, 129, 0.08) 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            pointerEvents: 'none',
            opacity: 0.8,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', sm: '3.4rem', md: '4.2rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.035em',
                color: '#0F172A',
                mb: 3,
              }}
            >
              Building Technology for{' '}
              <span className="text-gradient-emerald">Real Estate</span>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#334155',
                lineHeight: 1.7,
                mb: 3,
                fontWeight: 500,
              }}
            >
              Coalitionify Innovate Private Limited is a technology company focused on building software and digital solutions for the real estate industry.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: '#64748B',
                lineHeight: 1.7,
                maxWidth: 720,
                mx: 'auto',
              }}
            >
              We create practical technology that helps real estate businesses simplify operations, improve sales processes, automate workflows, and engage customers more effectively.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: '#64748B',
                lineHeight: 1.7,
                maxWidth: 720,
                mx: 'auto',
                mt: 2,
              }}
            >
              By combining software, AI, automation, and digital communication, we build solutions designed around the real challenges of modern real estate businesses.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 2. WHO WE ARE & 5 PRINCIPLES */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Grid2 container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Chip
                label="WHO WE ARE"
                sx={{
                  backgroundColor: '#ECFDF5',
                  color: '#059669',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  mb: 2,
                  border: '1px solid #A7F3D0',
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.7rem', md: '2.6rem' },
                  color: '#0F172A',
                  mb: 2.5,
                }}
              >
                An Innovation-Focused Technology Company
              </Typography>
              <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.7, mb: 3 }}>
                We combine deep technical capabilities with an acute understanding of real-world business environments. Our core focus spans software product engineering, artificial intelligence integration, communication automation, and business process workflow design.
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65 }}>
                Rather than building technology for technology's sake, Coalitionify Innovate designs solutions that seamlessly fit into daily commercial operations, allowing teams to respond faster, communicate smarter, and close more business.
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid rgba(16, 185, 129, 0.18)',
                  boxShadow: '0 20px 40px -10px rgba(16, 185, 129, 0.08)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 30px 60px -12px rgba(16, 185, 129, 0.12)',
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 3 }}>
                  Our 5 Core Principles
                </Typography>

                {[
                  { title: 'Practical', desc: 'Grounded in actual business workflows, not theoretical abstractions.', color: '#2563EB' },
                  { title: 'Intelligent', desc: 'Powered by purposeful AI that elevates customer communication.', color: '#059669' },
                  { title: 'Scalable', desc: 'Architected to support businesses from early stage to enterprise growth.', color: '#10B981' },
                  { title: 'User-Focused', desc: 'Designed with intuitive interfaces that demand zero operational friction.', color: '#0D9488' },
                  { title: 'Business-Driven', desc: 'Focused on tangible operational efficiency and measurable ROI.', color: '#2563EB' },
                ].map((p, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: idx < 4 ? 2.5 : 0 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        backgroundColor: `${p.color}15`,
                        color: p.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.82rem',
                        flexShrink: 0,
                        mt: 0.2,
                        border: `1px solid ${p.color}30`,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '1.05rem' }}>
                        {p.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.9rem', lineHeight: 1.5 }}>
                        {p.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 3. WHAT WE DO — 3 MAJOR AREAS */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 6, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Chip
              label="WHAT WE DO"
              sx={{
                backgroundColor: '#ECFDF5',
                color: '#059669',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
                border: '1px solid #A7F3D0',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1rem', md: '2.75rem' },
                color: '#0F172A',
                mb: 2,
                lineHeight: 1.2,
                whiteSpace: { xs: 'nowrap', md: 'normal' },
                width: { xs: 'max-content', md: 'auto' },
                maxWidth: 'none',
                mx: 'auto',
              }}
            >
              Three Strategic Pillars of Capabilities
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 640, mx: 'auto' }}>
              We organize our capabilities across three foundational domains of business technology.
            </Typography>
          </Box>

          <Grid2 container spacing={4}>
            {/* Pillar 1: Software Products */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Building2 size={26} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', mb: 1.5 }}>
                    Software Products
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Specialized software platforms designed to simplify industry-specific business operations.
                  </Typography>
                  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                    <Typography variant="caption" sx={{ color: '#059669', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                      FLAGSHIP PRODUCT
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      Acolead CRM
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
                      Real-estate-focused CRM platform for developers & agents.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid2>

            {/* Pillar 2: AI & Intelligent Automation */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      backgroundColor: 'rgba(16, 185, 129, 0.12)',
                      color: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Cpu size={26} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700,  fontSize: { xs: '1.25rem', md: '1.3rem' }, color: '#0F172A', mb: 1.5 }}>
                    AI & Intelligent Automation
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Deploy smart conversation engines and automation pipelines that operate 24/7 with zero lag.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['Social Media AI (IG & FB)', 'WhatsApp AI Automation', 'Intelligent Workflow Routing', 'AI Calling & Voice Solutions'].map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle2 size={16} style={{ color: '#10B981' }} />
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.875rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid2>

            {/* Pillar 3: Business Communication Technology */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                className="card-interactive"
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                  },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563EB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <MessageSquare size={26} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', mb: 1.5 }}>
                    Business Communication
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65, mb: 3 }}>
                    Connect direct messaging, customer touchpoints, and business communication infrastructure.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {['WhatsApp Business API', 'Social Media Engagement', 'Automated Customer Messaging', 'AI-Powered Lead Interaction'].map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle2 size={16} style={{ color: '#2563EB' }} />
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.875rem' }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 4. OUR APPROACH */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Chip
              label="OUR APPROACH"
              sx={{
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                color: '#2563EB',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1rem', md: '2.75rem' },
                color: '#0F172A',
                mb: 2,
              }}
            >
              Understand → Build → Automate → Grow
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 680, mx: 'auto', fontSize: { xs: '0.98rem', md: '1rem' }, lineHeight: 1.55 }}>
              A practical process for turning real business challenges into technology that creates lasting value.
            </Typography>
          </Box>

          <Grid2 container spacing={{ xs: 2, md: 3 }}>
            {[
              {
                title: 'Understand',
                desc: 'We identify business challenges, processes, and requirements.',
                icon: <Compass size={26} />,
                color: '#2563EB',
              },
              {
                title: 'Build',
                desc: 'We develop practical solutions around real business workflows.',
                icon: <Building2 size={26} />,
                color: '#059669',
              },
              {
                title: 'Automate',
                desc: 'We use AI and automation to simplify repetitive processes and improve efficiency.',
                icon: <Cpu size={26} />,
                color: '#0D9488',
              },
              {
                title: 'Grow',
                desc: 'Better technology helps businesses operate smarter and focus on sustainable growth.',
                icon: <Target size={26} />,
                color: '#7C3AED',
              },
            ].map((stage) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={stage.title}>
                <Paper
                  elevation={0}
                  className="card-interactive"
                  sx={{
                    p: { xs: 2, md: 3.5 },
                    height: '100%',
                    minHeight: { xs: 140, md: 220 },
                    borderRadius: 4,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateY(-4px)' },
                      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                      borderColor: `${stage.color}55`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 42, md: 56 },
                      height: { xs: 42, md: 56 },
                      borderRadius: 3,
                      backgroundColor: `${stage.color}14`,
                      color: stage.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1.5, md: 3 },
                      flexShrink: 0,
                    }}
                  >
                    {stage.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: '#0F172A',
                      mb: 0.75,
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      lineHeight: 1.15,
                    }}
                  >
                    {stage.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748B',
                      lineHeight: 1.45,
                      fontSize: { xs: '0.82rem', md: '0.875rem' },
                      display: '-webkit-box',
                      WebkitLineClamp: { xs: 3, md: 3 },
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {stage.desc}
                  </Typography>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 5. EXECUTIVE LEADERSHIP & OFFICE OF THE CEO */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC', borderTop: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="LEADERSHIP & FOUNDERS"
              sx={{
                backgroundColor: '#ECFDF5',
                color: '#059669',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
                border: '1px solid #A7F3D0',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', md: '2.75rem' },
                color: '#0F172A',
                mb: 2,
              }}
            >
              The People Behind the Vision
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 680, mx: 'auto' }}>
              Guiding the strategic vision, product engineering, and intelligent automation roadmap at Coalitionify Innovate.
            </Typography>
          </Box>

          {/* CEO Executive Spotlight Card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 5,
              backgroundColor: '#FFFFFF',
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 20px 45px -10px rgba(15, 23, 42, 0.06)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 30px 60px -12px rgba(15, 23, 42, 0.1)',
                borderColor: 'rgba(16, 185, 129, 0.2)',
              },
            }}
          >
            <Grid2 container spacing={{ xs: 4, md: 6 }} alignItems="center">
              <Grid2 size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 18, md: 24 },
                    left: '50%',
                    width: { xs: 260, md: 360 },
                    height: { xs: 150, md: 210 },
                    borderRadius: '48% 52% 45% 55%',
                    background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.95), rgba(239, 246, 255, 0.45))',
                    transform: 'translateX(-50%) rotate(-18deg)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 220, md: 280 },
                    height: { xs: 220, md: 280 },
                    borderRadius: '50%',
                    backgroundColor: '#EEF4FF',
                    border: '3px solid #DBEAFE',
                    boxShadow: '0 12px 32px -6px rgba(37, 99, 235, 0.18)',
                    p: 0,
                    mx: 'auto',
                    mb: 2,
                    transition: 'all 0.3s ease-in-out',
                    zIndex: 1,
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 16px 40px -8px rgba(15, 23, 42, 0.18)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/images/Untitled-4.png"
                    alt="Yogesh Patel, Founder of Coalitionify Innovate"
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'contain',
                      objectPosition: 'center bottom',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -0,
                      px: 1.7,
                      py: 0.3,
                      borderRadius: '16px',
                      backgroundColor: '#34D399',
                      color: '#070D19',
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      letterSpacing: '0.04em',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    FOUNDER & CTO
                  </Box>
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mt: 1 }}>
                  Yogesh Patel
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#059669', fontWeight: 700, fontSize: '0.85rem' }}>
                    Founder & CTO
                  </Typography>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/in/yogesh-patel-ays/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Yogesh Patel LinkedIn profile"
                    sx={{
                      backgroundColor: '#EAF3FF',
                      color: '#0A66C2',
                      width: 28,
                      height: 28,
                      '&:hover': {
                        backgroundColor: '#DDEBFF',
                      },
                    }}
                  >
                    <LinkedInIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5 }}>
                  Coalitionify Innovate Private Limited
                </Typography>
                <Box
                  sx={{
                    width: 62,
                    height: 3,
                    backgroundColor: '#2563EB',
                    mx: 'auto',
                    mt: 2,
                    mb: 1.5,
                    borderRadius: 2,
                  }}
                />
                <Typography sx={{ color: '#64748B', fontStyle: 'italic',  fontSize: { xs: '0.79rem', md: '0.9rem' }, lineHeight: 1.5 }}>
                  "Building technology that creates real impact."
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 8 }}>
                <Box sx={{ pl: { md: 2 } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: '#102047',
                      fontSize: { xs: '0.9rem', sm: '1.75rem' },
                      lineHeight: 1.3,
                      mb: 2,
                    }}
                  >
                    <Box component="span" sx={{ color: '#2563EB', mr: 1,  fontSize: { xs: '1.rem', md: '1.35rem' } }}>
                      “
                    </Box>
                    Technology must solve concrete business problems with{' '}
                    <Box component="span" sx={{ color: '#2563EB' }}>
                      zero friction.
                    </Box>
                    <Box component="span" sx={{ color: '#2563EB' }}>
                      ”
                    </Box>
                  </Typography>

                  <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.75, mb: 2.5, fontSize: '1.02rem' }}>
                    Yogesh Patel is a software engineer and technology entrepreneur focused on building practical software solutions for businesses.
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.7, mb: 3.5 }}>
                    He leads Coalitionify's technology and product vision, with a focus on solving real-world challenges in the real estate industry.
                  </Typography>

                  {/* Core Executive Focus Areas */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3.5 }}>
                    {[
                      { label: 'Strategic Product Architecture', icon: <Building2 size={14} /> },
                      { label: 'PropTech CRM Engineering', icon: <Building2 size={14} /> },
                      { label: 'Enterprise Automation Systems', icon: <Cpu size={14} /> },
                      { label: 'Scalable Communication APIs', icon: <MessageSquare size={14} /> },
                    ].map((area, i) => (
                      <Chip
                        key={i}
                        label={area.label}
                        icon={area.icon}
                        size="small"
                        sx={{
                          backgroundColor: '#EFF6FF',
                          color: '#1E3A8A',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          border: '1px solid rgba(15, 23, 42, 0.08)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#E2E8F0',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      />
                    ))}
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleNavigate('/contact')}
                    endIcon={<ArrowRight size={16} />}
                    sx={{
                      borderRadius: '10px',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.22)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                        boxShadow: '0 12px 28px rgba(37, 99, 235, 0.3)',
                      },
                    }}
                  >
                    Connect with Executive Team
                  </Button>
                </Box>
              </Grid2>
            </Grid2>
          </Paper>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 5. VISION & MISSION EDITORIAL STATEMENTS */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#070D19', color: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Grid2 container spacing={{ xs: 6, md: 8 }}>
            {/* Vision */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  borderRadius: 4,
                  backgroundColor: 'rgba(15, 23, 42, 0.7)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                    boxShadow: '0 20px 50px rgba(59, 130, 246, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Compass size={28} style={{ color: '#60A5FA' }} />
                  <Typography variant="caption" sx={{ color: '#60A5FA', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    OUR VISION
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.9rem', md: '2.4rem' },
                    color: '#FFFFFF',
                    mb: 3,
                    lineHeight: 1.25,
                  }}
                >
                  Building a Smarter Real Estate Industry
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8', lineHeight: 1.7 }}>
                  To become a trusted technology partner for real estate businesses by creating innovative, reliable, and practical software solutions.
                </Typography>
              </Box>
            </Grid2>

            {/* Mission */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  borderRadius: 4,
                  backgroundColor: 'rgba(15, 23, 42, 0.7)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(16, 185, 129, 0.5)',
                    boxShadow: '0 20px 50px rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Target size={28} style={{ color: '#10B981' }} />
                  <Typography variant="caption" sx={{ color: '#34D399', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    OUR MISSION
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '0.8rem', md: '2.4rem' },
                    color: '#FFFFFF',
                    mb: 3,
                    lineHeight: 1.25,
                  }}
                >
                  Simplifying Real Estate Through Technology
                </Typography>
                <Typography variant="body1" sx={{ color: '#94A3B8', lineHeight: 1.7 }}>
                  To understand real estate business challenges and transform them into simple, intelligent, and scalable technology solutions that improve efficiency and support growth.
                </Typography>
              </Box>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* ========================================================================= */}
      {/* 6. TEAM & CULTURE */}
      {/* ========================================================================= */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Chip
              label="TEAM & CULTURE"
              sx={{
                backgroundColor: '#ECFDF5',
                color: '#059669',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                mb: 2,
                border: '1px solid #A7F3D0',
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: '#0F172A',
                mb: 2,
              }}
            >
              Building Together. Growing Together.
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 760, mx: 'auto', lineHeight: 1.7 }}>
              Our team brings together technology, product, design, marketing, and business expertise to build solutions for real industry challenges.
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', maxWidth: 760, mx: 'auto', mt: 1.5, lineHeight: 1.7 }}>
              We believe in collaboration, innovation, customer focus, and continuous learning, creating a culture where people and ideas can grow together.
            </Typography>
          </Box>

          <Grid2 container spacing={3}>
            {[
              { title: 'Collaboration', desc: 'Different perspectives come together to solve meaningful business challenges.', icon: <MessageSquare size={24} />, color: '#2563EB' },
              { title: 'Innovation', desc: 'We explore better ideas and apply technology with practical business purpose.', icon: <Cpu size={24} />, color: '#059669' },
              { title: 'Customer Focus', desc: 'We stay close to the people and businesses our solutions are built to serve.', icon: <Building2 size={24} />, color: '#0D9488' },
              { title: 'Continuous Growth', desc: 'We keep learning, improving, and growing together as a team.', icon: <Target size={24} />, color: '#7C3AED' },
            ].map((value) => (
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={value.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.1, md: 3.5 },
                    height: '100%',
                    borderRadius: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                      borderColor: `${value.color}55`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      backgroundColor: `${value.color}14`,
                      color: value.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.65 }}>
                    {value.desc}
                  </Typography>
                </Paper>
              </Grid2>
            ))}
          </Grid2>
        </Container>
      </Box>

    </Box>
  );
};

export default AboutPage;